import { lazy, Suspense } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import Estimator from './Apps/Shared/Estimator';
import LoadingScreen from './Components/LoadingScreen/LoadingScreen';
import ProtectedRoute from './Components/ProtectedRoute';
import Redirector from './Redirector';
import { NOT_FOUND_PAGE } from './constants/environment';

const AmbassadorAttributionRoot = lazy(
    () =>
        import(
            './Apps/Shared/Payments/ClientViewPayments/InProgress/Ambassador/AmbassadorAttributions/AmbassadorAttributionRoot'
        ),
);
const CampaignRoot = lazy(() => import('./Apps/Shared/Campaigns/CampaignRoot'));
const CampaignsRoot = lazy(() => import('./Apps/Shared/Campaigns/CampaignsRoot'));
const NewCampaignsRoot = lazy(() => import('./Apps/Shared/NewCampaigns/CampaignsRoot'));
const CreateInfluencerCampaign = lazy(
    () =>
        import(
            './Apps/Shared/NewCampaigns/InfluencerCampaigns/ClientSide/CreateCampaign/CreateCampaign'
        ),
);
const EditInfluencerCampaign = lazy(
    () =>
        import(
            './Apps/Shared/NewCampaigns/InfluencerCampaigns/ClientSide/CreateCampaign/EditCampaign'
        ),
);
const DtcCampaignsRoot = lazy(
    () => import('./Apps/Shared/NewCampaigns/DTCCampaigns/DtcCampaignsRoot'),
);
const DtcCreateCampaign = lazy(
    () => import('./Apps/Shared/NewCampaigns/DTCCampaigns/ClientSide/DtcCreateCampaign'),
);
const DtcEditCampaign = lazy(
    () => import('./Apps/Shared/NewCampaigns/DTCCampaigns/ClientSide/DtcEditCampaign'),
);
const DtcCampaignPreview = lazy(
    () => import('./Apps/Shared/NewCampaigns/DTCCampaigns/ClientSide/DtcSetup/DtcCampaignPreview'),
);
const DtcCampaignAmbassadorPreview = lazy(
    () => import('./Apps/Shared/NewCampaigns/DTCCampaigns/AmbassadorSide/DtcCampaignPreview'),
);
const CampaignReportRoot = lazy(() => import('./Apps/Shared/CampaignReporting/CampaignReportRoot'));
const CampaignsReportingRoot = lazy(
    () => import('./Apps/Shared/CampaignReporting/CampaignsReportingRoot'),
);
const CampaignPreview = lazy(
    () => import('./Apps/Shared/Campaigns/HubCampaigns/ClientSideCampaigns/CampaignPreview'),
);
const DtcCampaignView = lazy(
    () =>
        import(
            './Apps/Shared/NewCampaigns/DTCCampaigns/ClientSide/DtcCampaignView/DtcCampaignViewRoot'
        ),
);
const NewCampaign = lazy(
    () =>
        import(
            './Apps/Shared/Campaigns/HubCampaigns/ClientSideCampaigns/CampaignWizard/CampaignWizard'
        ),
);
const EditCampaign = lazy(
    () =>
        import(
            './Apps/Shared/Campaigns/HubCampaigns/ClientSideCampaigns/CampaignWizard/CampaignWizard'
        ),
);

const Invitees = lazy(
    () =>
        import(
            './Apps/Shared/Campaigns/HubCampaigns/ClientSideCampaigns/CampaignInvitees/InvitessRoot'
        ),
);
const Dashboard = lazy(() => import('./Apps/Shared/Dashboard/Dashboard'));
const ProductSeeding = lazy(() => import('./Apps/Shared/ProductSeeding/ProductSeedingRoot'));

const CreateCatalog = lazy(
    () => import('./Apps/Shared/ProductSeeding/CatalogBuilder/CatalogBuilderRoot'),
);

const EditCatalog = lazy(
    () => import('./Apps/Shared/ProductSeeding/CatalogBuilder/CatalogEditorRoot'),
);

const ProductSeedingWizard = lazy(
    () => import('./Apps/Shared/ProductSeeding/ProductSeedingWizard/ProductSeedingWizardRoot'),
);
const ProductSeedingOrderDetails = lazy(
    () =>
        import(
            './Apps/Shared/ProductSeeding/ProductSeedingOrderDetails/ProductSeedingOrderDetailsRoot'
        ),
);

const ExternalBillingVerification = lazy(
    () =>
        import('./Apps/Shared/AppWorkflows/LegacyClientOnboardingFlow/ExternalBillingVerification'),
);
const Forbidden = lazy(() => import('./Components/Forbidden'));

const InfluencerProfile = lazy(
    () => import('./Components/InfluencerInfo/InfluencerProfile/ProfileRoot'),
);
const OnboardingLegacy = lazy(
    () => import('./Apps/Shared/AppWorkflows/LegacyClientOnboardingFlow/ClientOnboarding'),
);
const ActivityDetails = lazy(
    () => import('./Components/InfluencerInfo/InfluencerProfile/ActivityDetails'),
);
const InternalTools = lazy(() => import('./Apps/Shared/InternalTools/InternalTools'));

const LoginRoot = lazy(() => import('./Apps/Shared/Login/LoginRoot'));
const Logout = lazy(() => import('./Apps/Shared/Logout/Logout'));
const Messaging = lazy(() => import('./Apps/Shared/Messaging/MessagingRoot'));
const OAutConnect = lazy(() => import('./Apps/Shared/AppWorkflows/OAuthConnect/OAuthConnect'));
const PasswordReset = lazy(() => import('./Apps/Shared/PasswordReset/PasswordResetCont'));
const UnsubscribeUser = lazy(() => import('./Apps/Shared/EmailUnsubscribe/UnsubscribeUser'));
const PaymentsOnboarding = lazy(
    () => import('./Apps/Shared/AppWorkflows/PaymentsOnboarding/PaymentsOnboarding'),
);
const PaymentsRoot = lazy(() => import('./Apps/Shared/Payments/PaymentsRoot'));
const AmbassadorOrderRoot = lazy(
    () => import('./Apps/Shared/ProductSeeding/AmbassadorOrders/AmbassadorOrderRoot'),
);
const Posts = lazy(() => import('./Apps/Hub/Posts2/PostsRoot'));
const ProductsRoot = lazy(() => import('./Apps/Hub/Products/ProductsRoot'));
const ProfileRoot = lazy(() => import('./Apps/Shared/Profile/ProfileRoot'));
const Reporting = lazy(() => import('./Apps/Hub/Reporting/ReportingRoot'));
const ResetRequest = lazy(() => import('./Apps/Shared/ResetRequest/ResetRequest'));
const ContactsMain = lazy(() => import('./Apps/Hub/Contacts/ContactsMain'));
const UploadWizard = lazy(() => import('./Apps/Hub/Contacts/UploadWizard/UploadWizard'));
const CreateGroup = lazy(() => import('./Apps/Hub/Contacts/tabs/Groups/CreateGroup/CreateGroup'));
const SettingsRoot = lazy(() => import('./Apps/Shared/Settings/SettingsRoot'));
const SignupRoot = lazy(() => import('./Apps/Shared/Signup/SignupRoot'));
const TempJoin = lazy(() => import('./Apps/Shared/Signup/HubApplication/TempJoin'));
const AmbassadorHubEditorRoot = lazy(
    () =>
        import('./Apps/Shared/Settings/AmbassadorHubs/AmbassadorHubEditor/AmbassadorHubEditorRoot'),
);
const UploadView = lazy(() => import('./Apps/Shared/Uploader/UploaderRoot'));
const DiscountTemplateWizard = lazy(
    () => import('./Apps/Shared/DiscountCodes/TemplateWizard/DiscountTemplateWizard'),
);

const ClientShopDetails = lazy(
    () => import('./Apps/Shared/Settings/ClientShopsSettings/ShopDetails/ClientShopDetailsRoot'),
);

const ShopConnect = lazy(() => import('./Apps/Shared/Settings/ClientShopsSettings/ShopConnect'));
const Streaming = lazy(() => import('./Apps/Hub/Streaming/StreamingRoot'));
const FAQ = lazy(() => import('./Apps/Hub/FAQ'));
const ExternalRedirectWithAuth = lazy(() => import('./Components/ExternalAuthRedirect'));

const AOS = lazy(() => import('./Apps/Shared/ProductSeeding/AOS'));
const ContentApproval = lazy(
    () => import('./Apps/Shared/Messaging/ContentApproval/ContentApproval'),
);

const SequenceCustomization = lazy(() => import('./Apps/Shared/Sequences/SequenceCustomization'));

const DiscountCodes = lazy(() => import('./Apps/Shared/DiscountCodes/DiscountsRoot'));

const ScheduleDiscountUpdate = lazy(
    () => import('./Apps/Shared/DiscountCodes/ScheduleDiscountUpdate'),
);
const Recruit = lazy(() => import('./Apps/Hub/Recruit/RecruitRoot'));
const SignUpBrandRoot = lazy(() => import('./Apps/Shared/SignupBrand/SignupBrandRoot'));

const TikTokShop = lazy(
    () => import(/* webpackChunkName: "Tiktok Shop tab" */ './Apps/Hub/TiktokShop/TiktokShopRoot'),
);

const NewTargetCollaboration = lazy(
    () => import('./Apps/Hub/TiktokShop/Collaborations/TargetCollaborations/NewInvitation'),
);

const SavedTargetCollaboration = lazy(
    () =>
        import(
            './Apps/Hub/TiktokShop/Collaborations/TargetCollaborations/SavedInvitation/index.tsx'
        ),
);

const TargetedCollaborationOptInLandingPage = lazy(
    () =>
        import(
            /* webpackChunkName: "Tiktok Shop targeted collaboration pre opt in" */ './Apps/Hub/Products/AmbassadorSide/TikTokShopProducts/OptInLandingPage'
        ),
);

const CommunicationRoot = lazy(() => import('./Apps/Hub/Communication/CommunicationRoot'));

const InfluencerCampaignPublicPage = lazy(
    () =>
        import(
            './Apps/Shared/NewCampaigns/InfluencerCampaigns/Public/InfluencerCampaignPublicPage'
        ),
);
const EditSavedRuleSet = lazy(() => import('./Apps/Shared/DiscountCodes/RuleSets/index'));
const EmailVerified = lazy(
    () => import('./Apps/Shared/SignupBrand/SignupBrand/Email/EmailVerified'),
);
const EmailSent = lazy(() => import('./Apps/Shared/SignupBrand/SignupBrand/Email/EmailSent'));

// const DemoPosts = lazy(() => import(/* webpackChunkName: "DemoPosts" */ './AppSubmission/Posts'));
// const DemoInbox = lazy(() => import(/* webpackChunkName: "DemoInbox" */ './AppSubmission/Inbox'));
const AppSubmission = lazy(() => import(/* webpackChunkName: "AppSubmission" */ './AppSubmission'));
const DevTools = lazy(() => import('./Apps/Shared/DevTools/DevTools'));

const Routes = () => (
    <Router>
        <Suspense fallback={<LoadingScreen />}>
            <Switch>
                <Route path='/login' component={LoginRoot} />
                <Route path='/join' component={SignupRoot} />
                <Route path='/embeddable-join' component={TempJoin} />
                <Route path='/signup' component={SignupRoot} />
                <Route path='/brand-signup' component={SignUpBrandRoot} />
                <Route path='/enterprise-signup' component={SignUpBrandRoot} />
                <Route path='/aos/:token' component={AOS} />
                <Route path='/password/reset' component={PasswordReset} />
                <Route path='/password/request-reset' component={ResetRequest} />
                <Route path='/unsubscribe/:token' component={UnsubscribeUser} />
                <Route path='/billing-setup' component={ExternalBillingVerification} />
                <Route path='/uploader/:clientName' component={UploadView} />
                <Route path='/payments-setup' component={PaymentsOnboarding} />
                <Route path='/faq' component={FAQ} />
                <Route path='/logout' component={Logout} />
                <Route
                    path='/tiktok-shop/opt-in-landing'
                    component={TargetedCollaborationOptInLandingPage}
                />
                <Route path='/influencer-campaign/:uuid' component={InfluencerCampaignPublicPage} />
                <Route path='/email-sent' component={EmailSent} />
                <Route path='/verify-email' component={EmailVerified} />
                <Route path='/forbidden' component={Forbidden} />

                <ProtectedRoute exact path='/' component={Redirector} />
                <ProtectedRoute path='/app' component={AppSubmission} />
                <ProtectedRoute path='/external-auth-access' component={ExternalRedirectWithAuth} />
                <ProtectedRoute path='/tools' component={InternalTools} />
                <ProtectedRoute path='/oauth-connect' component={OAutConnect} />
                <ProtectedRoute path='/dashboard' component={Dashboard} />
                <ProtectedRoute
                    path='/ambassador-hub-editor/:id?'
                    component={AmbassadorHubEditorRoot}
                />
                <ProtectedRoute
                    path='/ambassador-orders/:ambassadorOrderId'
                    component={AmbassadorOrderRoot}
                />
                <ProtectedRoute path='/product-seeding' component={ProductSeeding} />
                <ProtectedRoute path='/create-catalog' component={CreateCatalog} />
                <ProtectedRoute path='/edit-catalog/:id' component={EditCatalog} />
                <ProtectedRoute
                    path='/product-seeding-order/:id'
                    component={ProductSeedingOrderDetails}
                />
                <ProtectedRoute path='/product-seeding-create' component={ProductSeedingWizard} />
                <ProtectedRoute path='/settings' component={SettingsRoot} />
                <ProtectedRoute path='/campaigns' component={CampaignsRoot} />
                <ProtectedRoute
                    path='/influencer-campaigns/create'
                    component={CreateInfluencerCampaign}
                />
                <ProtectedRoute
                    path='/influencer-campaigns/:id/edit'
                    component={EditInfluencerCampaign}
                />
                <ProtectedRoute path='/new-campaigns' component={NewCampaignsRoot} />
                <ProtectedRoute path='/dtc-campaigns/:id/edit' component={DtcEditCampaign} />
                <ProtectedRoute path='/dtc-campaigns/create' component={DtcCreateCampaign} />
                <ProtectedRoute path='/dtc-campaigns/:id/create' component={DtcCreateCampaign} />
                <ProtectedRoute path='/dtc-campaigns/:id/preview' component={DtcCampaignPreview} />
                <ProtectedRoute
                    path='/dtc-campaigns/:id/ambassador-preview'
                    component={DtcCampaignAmbassadorPreview}
                />
                <ProtectedRoute path='/dtc-campaigns/:id' component={DtcCampaignView} />
                <ProtectedRoute path='/dtc-campaigns' component={DtcCampaignsRoot} />
                <ProtectedRoute path='/ambassador-reporting' component={CampaignsReportingRoot} />
                <ProtectedRoute path='/invitees' component={Invitees} />
                <ProtectedRoute path='/ambassador-report/:id' component={CampaignReportRoot} />
                <ProtectedRoute path='/products' component={ProductsRoot} />
                <ProtectedRoute path='/posts' component={Posts} />
                <ProtectedRoute path='/campaign/:id' component={CampaignRoot} />
                <ProtectedRoute path='/campaign-preview/' component={CampaignPreview} />
                <ProtectedRoute path='/new-campaign' component={NewCampaign} />
                <ProtectedRoute path='/edit-campaign/:editId' component={EditCampaign} />
                <ProtectedRoute path='/contacts' component={ContactsMain} />
                <ProtectedRoute path='/upload-list' component={UploadWizard} />
                <ProtectedRoute path='/create-group' component={CreateGroup} />
                <ProtectedRoute path='/edit-group/:editId' component={CreateGroup} />
                <ProtectedRoute path='/reporting' component={Reporting} />
                <ProtectedRoute path='/messaging' component={Messaging} />
                <ProtectedRoute path='/influencer-profile/:id' component={InfluencerProfile} />
                <ProtectedRoute path='/home' component={InfluencerProfile} />
                <ProtectedRoute path='/activities/:type/:id' component={ActivityDetails} />
                <ProtectedRoute path='/profile' component={ProfileRoot} />
                <ProtectedRoute
                    path='/payments/attributions/:id'
                    component={AmbassadorAttributionRoot}
                />
                <ProtectedRoute path='/payments' component={PaymentsRoot} />
                <ProtectedRoute path='/shops/:clientShopId' component={ClientShopDetails} />
                <ProtectedRoute path='/shop-connect/:platform/:code' component={ShopConnect} />
                <ProtectedRoute
                    path='/discount-template/:templateId'
                    component={DiscountTemplateWizard}
                />
                <ProtectedRoute path='/discount-template' component={DiscountTemplateWizard} />
                <ProtectedRoute
                    path='/discount-code/schedule-update/:clientShopId/:jobId'
                    component={ScheduleDiscountUpdate}
                />
                <ProtectedRoute path='/streaming' component={Streaming} />
                <ProtectedRoute
                    path='/content-approval/:influencerId'
                    component={ContentApproval}
                />
                <ProtectedRoute path='/discount-codes' component={DiscountCodes} />
                <ProtectedRoute path='/communication' component={CommunicationRoot} />
                <ProtectedRoute
                    path='/sequence-customize/:templateId'
                    component={SequenceCustomization}
                />
                <ProtectedRoute path='/recruit' component={Recruit} />
                <ProtectedRoute path='/onboarding-legacy' component={OnboardingLegacy} />
                <ProtectedRoute
                    path='/tiktok-shop/target-collaborations/:id'
                    component={SavedTargetCollaboration}
                />
                <ProtectedRoute
                    path='/tiktok-shop/edit-invitation/:id'
                    component={NewTargetCollaboration}
                />
                <ProtectedRoute
                    path='/tiktok-shop/create-invitation'
                    component={NewTargetCollaboration}
                />
                <ProtectedRoute
                    path='/discount-code/rule-set/:ruleSetId'
                    component={EditSavedRuleSet}
                />
                <ProtectedRoute path='/tiktok-shop' component={TikTokShop} />
                <ProtectedRoute path='/dev-tools' component={DevTools} />
                <ProtectedRoute path='/estimate' component={Estimator} />
                <Route
                    component={() => {
                        window.location.href = NOT_FOUND_PAGE;
                        return null;
                    }}
                />
            </Switch>
        </Suspense>
    </Router>
);

export default Routes;
